/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import {
    StreamingSessionSetupSuccessEventInfo,
    StreamingBeginEventInfo,
    StreamingEndEventInfo,
    ExitStreamerOptionsBlock,
    ForceTouchModeSettingsBlock
} from '../project-interface.types';
import { CustomMessage } from './constants';
import { ProjectInterfaceConfig } from './project-interface.config';

@Injectable({
    providedIn: 'root'
})
export class ProjectInterfaceService {
    private logger: Logger;
    private closeOSC$: Subject<boolean>;
    private openIGOSystemSettings$: Subject<boolean>;
    private enableListenerSubject = new BehaviorSubject<boolean>(true);
    private quitStreaming$: Subject<boolean>;
    private sendCustomMessage$: Subject<CustomMessage>;
    private onCustomMessage$: Subject<CustomMessage>;
    private isNonEnglishKeyboardLayout$: Subject<boolean>;
    private exitStreamerFlowNotify$ = new Subject<boolean>();
    private statsTopOffset$: Subject<number> = new Subject<number>();

    private _streamingBegin$: Subject<StreamingBeginEventInfo>;
    private _streamingEnd$: Subject<StreamingEndEventInfo>;
    private _streamingSessionSetupSuccess$: Subject<StreamingSessionSetupSuccessEventInfo>;

    public checkIfShortcutIsPresentCallback: (shortcutCombo: (string | number)[]) => (string | number)[];
    /** Callers outside the Streamer pass options to name where it should exit to; without them the
     *  Streamer navigates to its own exit URL, which supersedes any navigation already in flight. */
    public exitStreamerCallBack: (options?: ExitStreamerOptionsBlock) => Observable<boolean>;
    public isNotificationAllowedCallBack: () => Observable<boolean>;
    public getForceTouchMode: () => ForceTouchModeSettingsBlock;

    constructor(loggingService: LoggingService) {
        this.logger = loggingService.getLogger('ProjectInterfaceService');
        this.closeOSC$ = new Subject();
        this.openIGOSystemSettings$ = new Subject();
        this.quitStreaming$ = new Subject();
        this.sendCustomMessage$ = new Subject();
        this.onCustomMessage$ = new Subject();
        this.isNonEnglishKeyboardLayout$ = new Subject<boolean>();
        this._streamingBegin$ = new Subject<StreamingBeginEventInfo>();
        this._streamingEnd$ = new Subject<StreamingEndEventInfo>();
        this._streamingSessionSetupSuccess$ = new Subject<StreamingSessionSetupSuccessEventInfo>();
    }

    public get streamingSessionSetupSuccess$(): Observable<StreamingSessionSetupSuccessEventInfo> {
        return this._streamingSessionSetupSuccess$.asObservable();
    }

    public get streamingBegin$(): Observable<StreamingBeginEventInfo> {
        return this._streamingBegin$.asObservable();
    }

    public get streamingEnd$(): Observable<StreamingEndEventInfo> {
        return this._streamingEnd$.asObservable();
    }

    public notifyStreamingSessionSetupSuccess(eventInfo: StreamingSessionSetupSuccessEventInfo): void {
        this._streamingSessionSetupSuccess$.next(eventInfo);
    }

    public notifyStreamingBegin(eventInfo: StreamingBeginEventInfo): void {
        this._streamingBegin$.next(eventInfo);
    }

    public notifyStreamingEnd(eventInfo: StreamingEndEventInfo): void {
        this._streamingEnd$.next(eventInfo);
    }

    public get triggerCloseIGO(): Observable<boolean> {
        return this.closeOSC$.asObservable();
    }

    public get triggerOpenIGOSystemSettings(): Observable<boolean> {
        return this.openIGOSystemSettings$.asObservable();
    }

    public get triggerQuitStreaming(): Observable<boolean> {
        return this.quitStreaming$.asObservable();
    }

    public get enableListener(): Observable<boolean> {
        return this.enableListenerSubject.asObservable();
    }

    public get sendCustomMessage(): Observable<CustomMessage> {
        return this.sendCustomMessage$.asObservable();
    }

    public get onCustomMessageReceived(): Observable<CustomMessage> {
        return this.onCustomMessage$.asObservable();
    }

    public get isNonEnglishKeyboardLayout(): Observable<boolean> {
        return this.isNonEnglishKeyboardLayout$.asObservable();
    }

    public get exitStreamerFlowNotify(): Observable<boolean> {
        return this.exitStreamerFlowNotify$.asObservable();
    }

    public notifyStreamerFlowExit() {
        this.exitStreamerFlowNotify$.next(true);
    }

    public setNotificationAllowedCallBack(isNotificationAllowed: () => Observable<boolean>): void {
        this.logger.verbose('Setting isNotificationAllowed Callback');
        this.isNotificationAllowedCallBack = isNotificationAllowed;
    }

    public setShortcutCheckCallBacks(
        checkIfShortcutIsPresent: (shortcutCombo: (string | number)[]) => (string | number)[]
    ): void {
        this.logger.verbose('Setting checkIfShortcutIsPresent Callback');
        this.checkIfShortcutIsPresentCallback = checkIfShortcutIsPresent;
    }

    public setExitStreamerCallBack(exitStreamer: (options?: ExitStreamerOptionsBlock) => Observable<boolean>): void {
        this.logger.verbose('Setting exitStreamer Callback');
        this.exitStreamerCallBack = exitStreamer;
    }

    resumeListening(): void {
        this.enableListenerSubject.next(true);
    }

    pauseListening(): void {
        this.enableListenerSubject.next(false);
    }

    quitStreaming(): void {
        this.quitStreaming$.next(true);
    }

    closeIGO(): void {
        this.closeOSC$.next(true);
    }

    openIGOSystemSettings(): void {
        this.openIGOSystemSettings$.next(true);
    }

    public setForceTouchModeCallback(getForceTouchMode: () => ForceTouchModeSettingsBlock): void {
        this.logger.verbose('Setting force touch mode callback');
        this.getForceTouchMode = getForceTouchMode;
    }

    public postCustomMessage(msg: string, recipient: string, messageType?: string) {
        const message: CustomMessage = {
            messageType: messageType || 'customMessage',
            messageRecipient: recipient,
            data: msg
        };
        this.logger.verbose('CustomMessage being sent:', message);
        this.sendCustomMessage$.next(message);
    }

    public onCustomMessage(msg: CustomMessage) {
        // remove the escape sequence else JSON parse would crash
        msg.data = msg.data.replace('\u0000', '');
        this.onCustomMessage$.next(msg);
    }

    setIsNonEnglishKeyboardLayout(isNativeKeyboard: boolean): void {
        this.isNonEnglishKeyboardLayout$.next(isNativeKeyboard);
    }

    setStatsTopOffset(offset: number) {
        this.statsTopOffset$.next(offset);
    }

    getStatsTopOffset(): Observable<number> {
        return this.statsTopOffset$.asObservable();
    }
}
